// Returns write a line into each stone's lifecycle ledger
// (itemCodes/{code}/history): "Returned to supplier" (purchase return,
// type 'return-out') or "Returned by customer" (credit note, 'return-in').
// Deleting the return must remove that line too, or Inventory › Activity
// and the Code history keep showing a return that no longer exists.
//
// New lines carry `returnId`; older ones are matched by stone, carat, date
// and the invoice number in their note.

import { collection, collectionGroup, getDocs, type DocumentReference, type Firestore } from 'firebase/firestore';
import type { PurchaseReturn, SalesReturn } from '../types';

export type ReturnKind = 'purchase' | 'sale';
const TYPE: Record<ReturnKind, string> = { purchase: 'return-out', sale: 'return-in' };

const normalizeCode = (code: string) => code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);

interface HistoryDoc { type?: string; returnId?: string; inventoryItemId?: string; carat?: number; date?: string; notes?: string }

type AnyReturn = (PurchaseReturn & { _kind?: 'purchase' }) | (SalesReturn & { _kind?: 'sale' });

/** The stone doc a return line's history entry points at. */
function lineItemId(kind: ReturnKind, l: { inventoryItemId: string; wholeItem: boolean; sliceId?: string }) {
  return kind === 'sale' && !l.wholeItem && l.sliceId ? l.sliceId : l.inventoryItemId;
}
const invoiceNoOf = (kind: ReturnKind, r: AnyReturn) =>
  kind === 'purchase' ? (r as PurchaseReturn).invoiceNo : (r as SalesReturn).saleInvoiceNo;

/** Does this (untagged, older) history line belong to this return? */
function legacyMatch(kind: ReturnKind, r: AnyReturn, h: HistoryDoc, itemId: string, carat: number) {
  return !h.returnId && h.inventoryItemId === itemId && Math.abs((h.carat ?? 0) - carat) < 1e-3
    && h.date === r.date && (h.notes ?? '').includes(invoiceNoOf(kind, r) ?? '\u0000');
}

/** The history lines one return wrote (read before its delete transaction). */
export async function findReturnHistoryRefs(db: Firestore, kind: ReturnKind, ret: AnyReturn): Promise<DocumentReference[]> {
  const refs: DocumentReference[] = [];
  const used = new Set<string>();
  for (const l of ret.lines as { code?: string; inventoryItemId: string; wholeItem: boolean; sliceId?: string; carat: number }[]) {
    if (!l.code) continue;
    const snap = await getDocs(collection(db, 'itemCodes', normalizeCode(l.code), 'history'));
    const itemId = lineItemId(kind, l);
    const hit = snap.docs.find(d => {
      const h = d.data() as HistoryDoc;
      if (h.type !== TYPE[kind] || used.has(d.ref.path)) return false;
      return h.returnId ? h.returnId === ret.id : legacyMatch(kind, ret, h, itemId, l.carat);
    });
    if (hit) { used.add(hit.ref.path); refs.push(hit.ref); }
  }
  return refs;
}

/** Return lines left in stone histories by returns that were deleted before
 *  this fix (or by any other path), for a one time clean up. */
export async function findOrphanReturnHistory(db: Firestore, kind: ReturnKind, existing: AnyReturn[]): Promise<DocumentReference[]> {
  const snap = await getDocs(collectionGroup(db, 'history'));
  const ids = new Set(existing.map(r => r.id));
  return snap.docs.filter(d => {
    if (d.ref.parent.parent?.parent.id !== 'itemCodes') return false;   // skus/{id}/history is not a stone history
    const h = d.data() as HistoryDoc;
    if (h.type !== TYPE[kind]) return false;
    if (h.returnId) return !ids.has(h.returnId);
    return !existing.some(r => (r.lines as { inventoryItemId: string; wholeItem: boolean; sliceId?: string; carat: number }[])
      .some(l => legacyMatch(kind, r, h, lineItemId(kind, l), l.carat)));
  }).map(d => d.ref);
}
